import { Injectable, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { SubjectUtils } from '../../../Common/Journodex/services/journodex-subjects.service';
import { RecipientSearchResultApiModel } from '../../../api/distributions/model/recipient-search-results.api.model';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { ContactApiModel, OutletApiModel, TargetingSearchResultApiModel } from '../../../api/targeting/model/targeting-search-result.api.model';
import { RecipientResultModel } from '../models/recipient.result.model';
import { TargetingResultModel } from '../models/targeting-result.model';
import { JournodexEntityFactory } from './journodex-entity.factory';
import { EmploymentFactory } from './employment.factory';
import { ContactModel } from '../../../Common/TargetingShared/model/contact.model';
import { OutletModel } from '../models/outlet.model';
import { LanguageTagEnum } from '../../../Core/Localization/enums/language-tag.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { EmploymentModel } from '../models/employment.model';


@Injectable()
export class TargetingResultFactory implements OnDestroy {
    locale: string = LanguageTagEnum.en_US;
    localeSubscription: Subscription;

    constructor(
        private journodexEntityFactory: JournodexEntityFactory,
        private employmentFactory: EmploymentFactory,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
    ) {
        this.localeSubscription = this.currentUserStateService.getDataReferenceLanguageTag().pipe(
            filter(x => !!x)
        ).subscribe(languageTag => {
            this.locale = languageTag;
        });
    }

    ngOnDestroy(): void {
        this.localeSubscription.unsubscribe();
    }

    // TODO: All the documention. Explain why Empty is needed
    createEmptyTargetingResult(): TargetingResultModel {
        return new TargetingResultModel();
    }

    async createTargetingResult(apiModel: TargetingSearchResultApiModel): Promise<RecipientResultModel> {
        if (apiModel.entityType === EntityType.CONTACT) {
            const targetingResult = new TargetingResultModel(apiModel);
            targetingResult.contactModel = await this.setContactModel(apiModel);
            targetingResult.subjectChips = SubjectUtils.convertSubjectsToChips(targetingResult.contactModel.subjects);
            if (apiModel.employments.length > 0) {
                targetingResult.selectedEmployment = await this.employmentFactory.create(apiModel.employments[0]);
                // use contact location when no outlet geography is provided
                if (!targetingResult.selectedEmployment.outlet?.geography && apiModel.contactInfo?.geography) {
                    if (!targetingResult.selectedEmployment.outlet) {
                        targetingResult.selectedEmployment.outlet = this.journodexEntityFactory.createEmptyOutlet();
                    }

                    targetingResult.selectedEmployment.outlet.geography =
                        new JournodexGeographyModel(
                            apiModel.contactInfo.geography,
                            this.locale,
                            this.localizationService
                        );
                }
            } else if (apiModel.numberOfEmployments > 0) {
                targetingResult.hasActiveNotice = true;
            } else {
                // It's a freelancer, let's fake an employment to give him a location
                targetingResult.selectedEmployment = this.employmentFactory.createEmpty();
                if (apiModel.contactInfo && apiModel.contactInfo.geography) {
                    // TODO: Investigate this outletmodel instantiation. Can use factory?
                    targetingResult.selectedEmployment.outlet = this.journodexEntityFactory.createEmptyOutlet();
                    targetingResult.selectedEmployment.outlet.geography =
                        new JournodexGeographyModel(
                            apiModel.contactInfo.geography,
                            this.locale,
                            this.localizationService
                        );
                }
            }
            targetingResult.setBadges();
            return targetingResult;
        } else {
            const targetingResult = new TargetingResultModel(apiModel);
            targetingResult.outletModel = await this.setOutletModel(apiModel);
            targetingResult.setBadges();
            return targetingResult;
        }
    }

    createEmptyRecipientResult(): RecipientResultModel {
        return new RecipientResultModel();
    }


    async createRecipientResult(apiModel: RecipientSearchResultApiModel): Promise<RecipientResultModel> {
        const recipientResult = new RecipientResultModel(apiModel);
        if (apiModel.entityType === EntityType.CONTACT) {
            recipientResult.contactModel = await this.setContactModel(apiModel);
            recipientResult.subjectChips = SubjectUtils.convertSubjectsToChips(recipientResult.contactModel.subjects);
        } else if (apiModel.entityType === EntityType.OUTLET) {
            recipientResult.outletModel = await this.setOutletModel(apiModel);
            recipientResult.subjectChips = SubjectUtils.convertSubjectsToChips(recipientResult.outletModel.subjects);
        }
        recipientResult.setBadges();
        return recipientResult;
    }

    fromContactModel(contact: ContactModel): TargetingResultModel {
        const result = new TargetingResultModel();
        result.entityType = EntityType.CONTACT;
        result.contactModel = contact;
        result.avatarUrl = contact.avatarUrl;
        result.firstName = contact.firstName;
        result.lastName = contact.lastName;
        result.contactName = result.firstName + ' ' + result.lastName;
        result.id = contact.id;
        result.isPrivate = !!contact.privateContactMetadata;
        result.numberOfEmployments = contact.employmentModels?.length ?? 0;
        if (contact.geography) {
            result.locationString = contact.geography.displayString;
        } else if (contact.selectedEmployment?.outlet?.geography) {
            result.locationString = contact.selectedEmployment.outlet.geography.displayString;
        }
        result.subjectChips = SubjectUtils.convertSubjectsToChips(contact.subjects);
        result.selectedEmployment = contact.selectedEmployment?.clone() || new EmploymentModel();
        result.setBadges();
        return result;
    }

    private setContactModel(apiResult: TargetingSearchResultApiModel): Promise<ContactModel> {
        const contactApiModel: ContactApiModel = apiResult.contactInfo;
        if (!contactApiModel.creationDate) {
            contactApiModel.creationDate = apiResult.createdDate;
        }

        if (!contactApiModel.modifiedDate) {
            contactApiModel.modifiedDate = apiResult.modifiedDate;
        }

        if (!contactApiModel.employments.length) {
            contactApiModel.employments = apiResult.employments;
        }
        return this.journodexEntityFactory.createContact(contactApiModel);
    }

    private setOutletModel(apiResult: TargetingSearchResultApiModel): Promise<OutletModel> {
        const outletApiModel: OutletApiModel = apiResult.outletInfo;
        if (!outletApiModel.creationDate) {
            outletApiModel.creationDate = apiResult.createdDate;
        }

        if (!outletApiModel.modifiedDate) {
            outletApiModel.modifiedDate = apiResult.modifiedDate;
        }
        return this.journodexEntityFactory.createOutlet(outletApiModel);
    }
}
